// src/pages/billing/InvoiceCreatePage.tsx

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useBillingInvoice, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';

export const InvoiceCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useBillingInvoice();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    billing_account_id: '',
    memo: '',
    due_date: '',
    customer_name: '',
    customer_email: '',
    auto_finalize: false,
  });
  const [lines, setLines] = useState([{ description: '', quantity: '1', unit_price: '', product_id: '' }]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineChange = (index: number, field: string, value: string) => {
    setLines((prev) => prev.map((line, i) => i === index ? { ...line, [field]: value } : line));
  };

  const addLine = () => {
    setLines((prev) => [...prev, { description: '', quantity: '1', unit_price: '', product_id: '' }]);
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await store.CreateInvoice({
        billing_account: { id: formData.billing_account_id },
        memo: formData.memo,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : undefined,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        lines: {
          lines: lines.map((l) => ({
            description: l.description,
            quantity: l.quantity,
            unit_price: { amount: Math.round(parseFloat(l.unit_price || '0') * 100), currency: 'CURRENCY_USD', decimal_places: 2 },
            product: l.product_id ? { id: l.product_id } : undefined,
          })),
        },
        auto_finalize: formData.auto_finalize,
      } as any);
      navigate('/billing/invoices');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/billing/invoices" className="text-muted-foreground hover:text-foreground">Invoices</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.create')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">Create Invoice</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Invoice Details</CardTitle>
              <CardDescription>Set up the invoice header information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input label="Billing Account ID" type="text" value={formData.billing_account_id} onChange={(e) => handleChange('billing_account_id', e.target.value)} placeholder="Billing account ID" required />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Customer Name" type="text" value={formData.customer_name} onChange={(e) => handleChange('customer_name', e.target.value)} placeholder="Customer name" />
                <Input label="Customer Email" type="email" value={formData.customer_email} onChange={(e) => handleChange('customer_email', e.target.value)} placeholder="customer@company.com" />
              </div>
              <Input label="Due Date" type="date" value={formData.due_date} onChange={(e) => handleChange('due_date', e.target.value)} />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Memo</label>
                <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[80px]" value={formData.memo} onChange={(e) => handleChange('memo', e.target.value)} placeholder="Invoice memo" rows={3} />
              </div>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={formData.auto_finalize} onChange={(e) => handleChange('auto_finalize', e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
                <span className="text-sm font-medium text-foreground">Auto-finalize after creation</span>
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Line Items</CardTitle>
              <CardDescription>Add products or custom line items</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {lines.map((line, index) => (
                <div key={index} className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Input label={index === 0 ? 'Description' : undefined} type="text" value={line.description} onChange={(e) => handleLineChange(index, 'description', e.target.value)} placeholder="Description" required />
                  </div>
                  <div className="w-24">
                    <Input label={index === 0 ? 'Qty' : undefined} type="text" value={line.quantity} onChange={(e) => handleLineChange(index, 'quantity', e.target.value)} placeholder="1" />
                  </div>
                  <div className="w-32">
                    <Input label={index === 0 ? 'Unit Price' : undefined} type="text" value={line.unit_price} onChange={(e) => handleLineChange(index, 'unit_price', e.target.value)} placeholder="0.00" required />
                  </div>
                  <div className="w-40">
                    <Input label={index === 0 ? 'Product ID' : undefined} type="text" value={line.product_id} onChange={(e) => handleLineChange(index, 'product_id', e.target.value)} placeholder="Optional" />
                  </div>
                  {lines.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(index)}>Remove</Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="secondary" onClick={addLine}>Add Line Item</Button>
            </CardContent>
            <CardFooter className="flex justify-end gap-3">
              <Link to="/billing/invoices"><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('common.creating') : 'Create Invoice'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
});

export default InvoiceCreatePage;
